/*
  autoMergeLevel3:
    - merges 2 level of substate
    - skips substate if already modified
    - this is essentially redux-persist v4 behavior
*/
import { StateReconciler } from 'redux-persist';

import type { State } from './reducers';

const isPlainEnoughObject = (o: unknown): boolean => o !== null && !Array.isArray(o) && typeof o === 'object';

const isStateKey = (key: string, state: State): key is keyof State => key in state;

// True only for `{}`-literal objects. Date / Array / Immutable.Set and any class instance
// return false, so deepMergePreferInbound treats them as opaque leaves (taken wholesale)
// rather than recursing into them — critical for `booking.*.date` (a Date) and
// `booking.*.tripDetail` payloads.
const isPlainObject = (o: unknown): o is Record<string, unknown> => {
  if (o === null || typeof o !== 'object') return false;
  const proto = Object.getPrototypeOf(o);
  return proto === Object.prototype || proto === null;
};

// Recursive merge where a *defined* persisted (inbound) leaf wins and the server form
// (origin) fills every gap. Recurses only through plain objects, so an empty persisted
// sub-object (e.g. `vehicle: {}` serialized from `{ kind: undefined, color: undefined }`)
// contributes nothing and the server's `vehicle: { kind: "Ferrari" }` survives — the
// shallow `withoutUndefined` merge of 1.107.x got this wrong and left the info-step
// Continuer button disabled after login.
const deepMergePreferInbound = (origin: object, inbound: object): object =>
  isPlainObject(origin) && isPlainObject(inbound)
    ? Object.entries(inbound)
        // an unset persisted field must not clobber a server value
        .filter(([, value]) => value !== undefined)
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: isPlainObject(value) && isPlainObject(acc[key]) ? deepMergePreferInbound(acc[key], value) : value,
          }),
          origin,
        )
    : inbound;

export const autoMergeLevel3: StateReconciler<State> = (inboundState, originalState, reducedState, { debug }) => {
  // only rehydrate if inboundState exists and is an object
  if (inboundState && typeof inboundState === 'object') {
    return Object.keys(inboundState).reduce((acc, key) => {
      if (key === '_persist' || !isStateKey(key, acc)) return acc;

      if (originalState[key] !== reducedState[key]) {
        if (process.env.NODE_ENV !== 'production' && debug) {
          // eslint-disable-next-line no-console
          console.log('redux-persist/stateReconciler: sub state for key `%s` modified, skipping.', key);
          return acc;
        }
      }

      if (isPlainEnoughObject(reducedState[key])) {
        // Persisted state (inbound, from localStorage) wins for any value the user has set,
        // so selections survive a /?keep=true round-trip. originalState (origin, from the
        // server-rendered form) provides fallback values for fields the persisted state
        // doesn't define yet (e.g. an auto-assigned checkpoint on first visit, or the site
        // bootstrap in fast.js where initialState only has `site`). Filtering `undefined`
        // out of `inbound` prevents unset client fields from clobbering server-provided
        // ones. Server can still force a full reset via the `overrideLocalStorage` hidden
        // input handled in full.js.
        switch (key) {
          case 'booking':
          case 'information': {
            // User-driven, deeply nested sub-trees (booking has departure/arrival, each with
            // a `date`/`tripDetail` ; information has address/vehicle). A defined persisted
            // leaf wins so the user's choices survive a /?keep=true round-trip or a refresh,
            // while the server form fills gaps. The deep recursion is what stops an empty
            // persisted sub-object (`vehicle: {}` / `address: {}`, serialized from all-
            // undefined fields on /cart/services) from shadowing a server-filled one.
            const merged = deepMergePreferInbound(originalState[key] ?? {}, inboundState[key] ?? {});
            return { ...acc, [key]: { ...acc[key], ...merged } };
          }
          case 'cart':
            // The server-rendered cart form (origin) is authoritative for the whole `cart`
            // slice on every step load, so we never let the persisted (localStorage) cart win
            // here. Letting it win allowed a stale localStorage to overwrite the server's
            // authoritative cart and — via onRehydrate's persistCart re-assertion — silently
            // drop a confirmed option (e.g. the paid "Modification & Annulation") on the normal
            // forward flow whenever localStorage was out of sync (reproduced on Chrome:
            // services pushed back empty, hasCancellationOption recomputed false at payment).
            // Restoring the cart across a /?keep=true round-trip is intentionally given up until
            // the checkout-tunnel refactor; dates & personal info still survive via the
            // booking/information cases above.
            return { ...acc, [key]: { ...acc[key], ...originalState[key] } };
          default:
            return { ...acc, [key]: { ...acc[key], ...inboundState[key] } };
        }
      }

      return { ...acc, [key]: inboundState[key] };
    }, reducedState);
  }

  return reducedState;
};

export default autoMergeLevel3;
